/**
 * Gift / bonus line-item reconciliation (mystery gift, gift box, extra keyring,
 * orphan personalization cleanup, clear all-free carts).
 *
 * Ported out of sections/cart-drawer-tw.liquid (cart-slideout-to-summary-popup-plan.md
 * Phase 3) so gift logic survives after the jQuery cart drawer is removed. Listens on
 * `cart:updated` the same way the drawer script did; mutates cart via
 * window.batchUpdateCart (tw_js.liquid) when needed.
 */
(function () {
  'use strict';

  let isRunning = false;
  // Set when checkout is requested while a reconcile pass is already running - that pass may end
  // without redirecting (e.g. the clear-cart branch), so the next pass must pick it up.
  let checkoutRequested = false;

  function finishRun() {
    isRunning = false;
    if (checkoutRequested) {
      checkoutRequested = false;
      cartUpdates();
    }
  }

  // Every cart call of the checkout is done: take the loading down, then leave for checkout.
  function goToCheckout() {
    window._redirectToCheckout = false;
    clearLoadingForCache();
    // Summary popups free their checkout button (shared/cart-summary/useCheckoutLock.js).
    document.dispatchEvent(new CustomEvent('checkout:done'));
    window.location.href = '/checkout';
  }
  window.goToCheckout = goToCheckout;

  // Whole-page checkout loading (body.checkout-loading, tw_css.liquid): from the checkout click
  // until its cart calls are done (goToCheckout) - over the summary panel too, so nothing can be
  // clicked meanwhile. A checkout that stops instead (an error toast - every failed add/validation shows
  // one -, a bfcache restore, or nothing happening for a while) takes it down again.
  const CHECKOUT_LOADING_CLASS = 'checkout-loading';
  // Long enough for a slow photo-charm upload (bundle-builder-v2.jsx waits up to 15s + 30s retries).
  const CHECKOUT_LOADING_MAX_MS = 60000;
  let checkoutLoadingTimer = null;

  function showCheckoutLoading() {
    document.body.classList.add(CHECKOUT_LOADING_CLASS);
    clearTimeout(checkoutLoadingTimer);
    checkoutLoadingTimer = setTimeout(hideCheckoutLoading, CHECKOUT_LOADING_MAX_MS);
  }

  function hideCheckoutLoading() {
    clearTimeout(checkoutLoadingTimer);
    document.body.classList.remove(CHECKOUT_LOADING_CLASS);
  }

  // A checkout that failed: every loading state it put up comes down (the page loading, the
  // builders' `loading_animation` + summary overlay - an open summary panel keeps the overlay as
  // its backdrop), the redirect flag is cleared, and `checkout:stopped` tells the builders to drop
  // their own redirect flag - otherwise the next plain Add to cart would jump to checkout.
  function stopCheckout() {
    hideCheckoutLoading();
    window._redirectToCheckout = false;
    document.body.classList.remove('loading_animation');
    const overlay = document.querySelector('.js-summary-overlay');
    if (overlay && !document.querySelector('.js-summary-panel.open')) overlay.style.display = 'none';
    document.dispatchEvent(new CustomEvent('checkout:stopped'));
  }

  window.showCheckoutLoading = showCheckoutLoading;
  window.hideCheckoutLoading = hideCheckoutLoading;
  window.stopCheckout = stopCheckout;

  // Every failed checkout step shows an error toast (shared/notify.js notifyError) - while a
  // checkout is in flight, that toast is what stops it.
  document.addEventListener('exchange-life:notify', function (event) {
    if ((event.detail && event.detail.type) !== 'error') return;
    if (document.body.classList.contains(CHECKOUT_LOADING_CLASS) || window._redirectToCheckout) stopCheckout();
  });

  function clearLoadingForCache() {
    hideCheckoutLoading();
    document.body.classList.remove('loading_animation');
    const overlay = document.querySelector('.js-summary-overlay');
    if (overlay && !document.querySelector('.js-summary-panel.open')) overlay.style.display = 'none';
  }

  // Safety net for Back from checkout (bfcache): no loading left in the cached page - pagehide
  // changes are what the bfcache keeps, pageshow clears it again in case a browser kept an earlier
  // snapshot.
  window.addEventListener('pagehide', clearLoadingForCache);
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) clearLoadingForCache();
  });

  // Bail-outs below that can't reconcile gifts must still honor a pending checkout request
  // (reconcileGiftsAndCheckout / the builders set the flag and rely on this file to redirect) -
  // otherwise Checkout does nothing and the flag lingers until some unrelated cart:updated.
  function redirectIfCheckoutRequested() {
    if (!window._redirectToCheckout) return;
    goToCheckout();
  }

  function cartUpdates(event) {
    // TEMP DEBUG (checkout-redirect troubleshooting - safe to remove once resolved).
    console.log('[cart-gift-reconciliation][debug] cartUpdates called, isRunning =', isRunning, 'window._redirectToCheckout =', window._redirectToCheckout);
    if (isRunning) {
      console.log('[cart-gift-reconciliation][debug] skipped - already running');
      return;
    }
    // Background removals from the summary popups (shared/cart-summary/cartMutationQueue.js)
    // skip reconciliation: gift/bonus lines are hidden there and reconciled once at checkout
    // (reconcileGiftsAndCheckout below), so removing items never makes gift lines jump around.
    if (event && event.detail && event.detail.__deferGiftReconcile && !window._redirectToCheckout) {
      return;
    }
    try {
      // Set by hydrate-time background cart cleanup (bundle-builder-v2.jsx / charm-holder-
      // builder.jsx) right after a customer comes straight back from checkout to the same PDP -
      // that cleanup is meant to be invisible, so any gift/bonus reconciliation it triggers here
      // must stay invisible too, instead of popping the same loading_animation overlay a real
      // user-initiated add-to-cart/checkout would show.
      const silent = Boolean(event && event.detail && event.detail.__silentReconcile);
      const cartData = window.cartJSON || window.cart;
      if (!cartData || !cartData.items || !Array.isArray(cartData.items)) {
        console.warn('[cart-gift-reconciliation] Invalid cart data:', cartData);
        redirectIfCheckoutRequested();
        return;
      }

      // Subscription lines (e.g. the travel community membership, sold with a selling plan)
      // never count toward gifts - buying only the membership must not add a mystery gift etc.
      const isSubscriptionLine = (item) => !!(item && item.selling_plan_allocation);

      // Subtotal 0 but still has lines → only free items → clear cart. Skipped when a
      // subscription is in the cart: a free-trial plan can legitimately make the total 0.
      if (
        cartData.items.length > 0 &&
        (cartData.total_price ?? 0) === 0 &&
        !cartData.items.some(isSubscriptionLine)
      ) {
        const clearUrl =
          (window.routes && window.routes.cart && window.routes.cart.clear) || '/cart/clear';
        isRunning = true;
        fetch(`${clearUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        })
          .then(() => {
            if (typeof window.update_cart_content === 'function') {
              return window.update_cart_content('silent');
            }
            return null;
          })
          .catch((err) => console.error('[cart-gift-reconciliation] clearCart failed:', err))
          .finally(finishRun);
        return;
      }

      const addItems = [];
      const updateItems = [];

      const config = {
        autoAddMysteryGift: window.autoAddMysteryGift,
        mysteryShippingPrice: window.mysteryShippingPrice || 0,
        mysteryVariantId: window.mysteryVariantId || null,
        giftBoxId: window.giftBoxId || 49705259893079,
        extraKeyringId: window.extraKeyringId || 51926914662743,
        keychainProductId: window.keychainProductId || 9604548985175,
        shippingInsuranceVariantId: window.shippingInsuranceVariantId || 48981080375639,
        personalizationVariantId: window.personalizationVariantId || 49192406286679,
      };

      const isExcludedFromGiftEligibility = (item) => {
        if (!item) return true;
        if (isSubscriptionLine(item)) return true;
        const itemVariantId = item.variant_id || (item.variant && item.variant.id);
        const itemProductId = item.product_id || (item.product && item.product.id);
        const insuranceId = config.shippingInsuranceVariantId;

        if (itemVariantId === config.mysteryVariantId) return true;
        if (itemVariantId === config.giftBoxId) return true;
        if (itemVariantId === config.extraKeyringId) return true;
        if (itemVariantId === config.personalizationVariantId) return true;
        if (itemVariantId === insuranceId || itemProductId === insuranceId) return true;
        if (item.properties && item.properties.__add_ons) return true;
        if (item.properties && item.properties.__mystery_gift) return true;
        return false;
      };

      if (!config.mysteryVariantId) {
        console.warn('[cart-gift-reconciliation] Mystery variant ID not available');
        redirectIfCheckoutRequested();
        return;
      }

      let hasMysteryVariant = false;
      let mysteryVariantKey = '';
      let mysteryGiftQuantity = 0;
      let charmsCount = 0;
      let hasGiftBox = false;
      let giftBoxKey = '';
      let giftBoxQuantity = 0;
      let hasExtraKeyring = false;
      let extraKeyringKey = '';
      let extraKeyringQuantity = 0;
      let keychainQuantity = 0;
      let otherCharmHolderCount = 0;
      const personalizationKeys = [];
      // Personalization lines carrying a __group, and how many engraved units each __group's
      // owner line(s) actually hold - used below to drop orphans / fix quantity drift.
      const groupedPersonalizationItems = [];
      const engravedQtyByGroup = new Map();
      const hasEngravingLines = (props) =>
        Object.keys(props).some(
          (key) => /^Engraving Line \d+$/.test(key) && String(props[key] || '').trim() !== ''
        );

      cartData.items.forEach((item) => {
        if (!item) return;

        const itemVariantId = item.variant_id || (item.variant && item.variant.id);
        const itemProductId = item.product_id || (item.product && item.product.id);
        const props = item.properties || {};

        if (itemVariantId === config.personalizationVariantId) {
          personalizationKeys.push(item.key);
          if (props.__group) groupedPersonalizationItems.push(item);
        } else if (props.__group && hasEngravingLines(props)) {
          const group = String(props.__group);
          engravedQtyByGroup.set(group, (engravedQtyByGroup.get(group) || 0) + (item.quantity || 0));
        }

        if (itemVariantId === config.mysteryVariantId) {
          hasMysteryVariant = true;
          mysteryVariantKey = item.key;
          mysteryGiftQuantity = item.quantity || 0;
        }
        if (item.properties && item.properties.__type === 'charm_set') {
          charmsCount += item.quantity || 0;
        }

        if (itemVariantId === config.giftBoxId) {
          hasGiftBox = true;
          giftBoxKey = item.key;
          giftBoxQuantity = item.quantity || 0;
        }

        if (itemVariantId === config.extraKeyringId) {
          hasExtraKeyring = true;
          extraKeyringKey = item.key;
          extraKeyringQuantity = item.quantity || 0;
        }

        if (itemProductId === config.keychainProductId) {
          keychainQuantity += item.quantity || 0;
        }

        if (
          item.properties &&
          item.properties.__type === 'charm_holder' &&
          itemProductId !== config.keychainProductId
        ) {
          otherCharmHolderCount += item.quantity || 0;
        }
      });

      if (charmsCount === 0 && personalizationKeys.length > 0) {
        personalizationKeys.forEach((key) => {
          updateItems.push({ id: key, quantity: 0 });
        });
      } else {
        // Each Personalization line must match the engraved line sharing its __group: remove it
        // when that line is gone (e.g. charm removed from the cart summary popup), and clamp its
        // quantity to the engraved quantity otherwise. Lines without __group are left alone.
        const allocatedByGroup = new Map();
        groupedPersonalizationItems.forEach((item) => {
          const group = String(item.properties.__group);
          const remaining = Math.max(
            0,
            (engravedQtyByGroup.get(group) || 0) - (allocatedByGroup.get(group) || 0)
          );
          const expectedQty = Math.min(item.quantity || 0, remaining);
          allocatedByGroup.set(group, (allocatedByGroup.get(group) || 0) + expectedQty);
          if (expectedQty !== item.quantity) {
            updateItems.push({ id: item.key, quantity: expectedQty });
          }
        });
      }

      const realProductsCount = cartData.items.filter(
        (item) => !isExcludedFromGiftEligibility(item)
      ).length;

      if (config.autoAddMysteryGift) {
        const shouldAddMysteryGift = realProductsCount > 0;

        if (!shouldAddMysteryGift) {
          if (hasMysteryVariant) {
            updateItems.push({ id: mysteryVariantKey, quantity: 0 });
          }
        } else if (!hasMysteryVariant) {
          addItems.push({
            id: config.mysteryVariantId,
            quantity: 1,
            properties: { __mystery_gift: 'true' },
          });
        }
      }

      if (charmsCount >= 5) {
        if (!hasGiftBox) {
          addItems.push({
            id: config.giftBoxId,
            quantity: 1,
            properties: { __mystery_gift: 'true' },
          });
        }
      } else if (hasGiftBox) {
        updateItems.push({ id: giftBoxKey, quantity: 0 });
      }

      if (mysteryGiftQuantity > 1) {
        updateItems.push({ id: mysteryVariantKey, quantity: 1 });
      }

      if (giftBoxQuantity > 1) {
        updateItems.push({ id: giftBoxKey, quantity: 1 });
      }

      let requiredKeyrings = 0;
      if (keychainQuantity > 0 && charmsCount >= 20) {
        const charmsAfterKeychain = Math.max(0, charmsCount - keychainQuantity * 20);
        const charmKeyrings = Math.floor(charmsAfterKeychain / 20);
        requiredKeyrings = Math.max(
          0,
          keychainQuantity + charmKeyrings - otherCharmHolderCount
        );
      }
      if (requiredKeyrings > 0) {
        if (!hasExtraKeyring) {
          addItems.push({
            id: config.extraKeyringId,
            quantity: requiredKeyrings,
            properties: { __mystery_gift: 'true' },
          });
        } else if (extraKeyringQuantity !== requiredKeyrings) {
          updateItems.push({ id: extraKeyringKey, quantity: requiredKeyrings });
        }
      } else if (hasExtraKeyring) {
        updateItems.push({ id: extraKeyringKey, quantity: 0 });
      }

      // TEMP DEBUG (checkout-redirect troubleshooting - safe to remove once resolved).
      console.log('[cart-gift-reconciliation][debug] decision point - addItems =', addItems, 'updateItems =', updateItems, 'window._redirectToCheckout =', window._redirectToCheckout, 'silent =', silent);

      if (addItems.length > 0 || updateItems.length > 0) {
        if (typeof window.batchUpdateCart === 'function') {
          console.log('[cart-gift-reconciliation][debug] calling batchUpdateCart - awaiting its actual completion before clearing isRunning (batchUpdateCart itself checks window._redirectToCheckout again once its own cascade settles, so the redirect can happen from either place)');
          isRunning = true;
          // batchUpdateCart now RETURNS its full promise chain (add/update fetches +
          // update_cart_content) - await that instead of a blind setTimeout(0) before clearing
          // isRunning. The old fixed-delay guess raced against real network latency: on Safari,
          // the fetch cascade could settle AFTER the timeout fired (leaving isRunning stuck true
          // and silently dropping the next legitimate cart:updated pass) or BEFORE it (leaving a
          // stale isRunning=true window that dropped the very cart:updated dispatched by this
          // cascade's own update_cart_content call) - either way, this bundle-builder-v2/
          // charm-holder-builder checkout flow's window._redirectToCheckout could end up never
          // acted on, stranding the customer on a loading overlay with no redirect. Awaiting the
          // real promise removes the race entirely.
          Promise.resolve(window.batchUpdateCart(addItems, updateItems, silent))
            .catch((err) => console.error('[cart-gift-reconciliation] batchUpdateCart failed:', err))
            .finally(() => {
              console.log('[cart-gift-reconciliation][debug] batchUpdateCart cascade settled, isRunning cleared');
              finishRun();
            });
        } else {
          console.warn('[cart-gift-reconciliation] batchUpdateCart unavailable');
        }
      } else if (window._redirectToCheckout) {
        console.log('[cart-gift-reconciliation][debug] no gift/bonus changes needed - redirecting to /checkout now');
        goToCheckout();
      } else {
        console.log('[cart-gift-reconciliation][debug] no gift/bonus changes needed AND _redirectToCheckout is falsy - nothing to do');
      }
    } catch (error) {
      console.error('[cart-gift-reconciliation] Error in cartUpdates:', error);
      isRunning = false;
      // A checkout waiting on this pass would otherwise hang: say so and stop it (the error toast
      // stops it - see the exchange-life:notify listener above).
      if (window._redirectToCheckout) {
        const common = (window.translations && window.translations.bundle_builder && window.translations.bundle_builder.common) || {};
        document.dispatchEvent(new CustomEvent('exchange-life:notify', {
          detail: { message: common.add_failed || 'Could not add your selections to the cart. Please try again.', type: 'error' },
        }));
      }
    }
  }

  window.cartUpdates = cartUpdates;

  // Checkout from a summary popup with nothing new to add: reconcile gift/bonus lines, then
  // redirect. The redirect itself happens in cartUpdates() (no changes needed) or in
  // batchUpdateCart() once its changes settle - both check window._redirectToCheckout.
  window.reconcileGiftsAndCheckout = function () {
    window._redirectToCheckout = true;
    if (isRunning) {
      checkoutRequested = true;
      return;
    }
    cartUpdates();
  };

  function boot() {
    cartUpdates();
  }

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    boot();
  } else {
    document.addEventListener('DOMContentLoaded', boot);
  }

  document.addEventListener('cart:updated', function (event) {
    cartUpdates(event);
  });
})();
